import './RollsStatus.scss';

import { Trophy, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useAppSelector } from '@/app/store/hooks';
import {
  selectCountdown,
  selectPlayers,
  selectRollsHistory,
  selectRollsPotsByCurrency,
  selectRollsStatus,
  selectTotalPot,
  selectWinnerId,
} from '@/entities/rolls';
import { RollsPlayer, RollsStatus as RollsStatusEnum } from '@/entities/rolls';
import { selectUser } from '@/entities/user';
import { GAME_CONFIG } from '@/shared/config/constants';
import { formatCurrencyBreakdown, formatUsd } from '@/shared/lib/currency';
import { PlayerAvatar } from '@/shared/ui/PlayerAvatar';

const FEE_PCT = GAME_CONFIG.ROLLS.PLATFORM_FEE_PERCENT;
const FEE_LABEL = `${Math.round(FEE_PCT * 100)}% platform fee applied`;
const STATUS_EXIT_MS = 250;

// Mimics AnimatePresence mode="wait": when `status` changes, keeps rendering
// the previous status (with an exiting flag) for STATUS_EXIT_MS before
// swapping to the new one, so the old content can play its exit animation.
function useExitAwareStatus(status: RollsStatusEnum) {
  const [displayed, setDisplayed] = useState(status);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (status === displayed) return;
    setExiting(true);
    const timeout = setTimeout(() => {
      setDisplayed(status);
      setExiting(false);
    }, STATUS_EXIT_MS);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return { displayed, exiting };
}

function getRankLabel(payout: number): string {
  if (payout >= 50) return 'INSANE JACKPOT! 🚀';
  if (payout >= 10) return 'Big Win! 🎉';
  return 'Nice Catch! 🎯';
}

function useCountUp(target: number, duration = 1000) {
  const [value, setValue] = useState(0);
  const [done, setDone] = useState(false);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (target === 0) return;
    setValue(0);
    setDone(false);
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setValue(target);
        setDone(true);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);

  return { value, done };
}

interface RollsStatusBarProps {
  spinPlayer?: RollsPlayer | null;
}

export function RollsStatusBar({ spinPlayer = null }: RollsStatusBarProps) {
  const rawStatus = useAppSelector(selectRollsStatus);
  const countdown = useAppSelector(selectCountdown);
  const totalPot = useAppSelector(selectTotalPot);
  const potsByCurrency = useAppSelector(selectRollsPotsByCurrency);
  const winnerId = useAppSelector(selectWinnerId);
  const players = useAppSelector(selectPlayers);
  const history = useAppSelector(selectRollsHistory);
  const myUser = useAppSelector(selectUser);

  const winner = players.find((p) => p.id === winnerId);
  const lastPayout = history[0]?.winnerPayout ?? totalPot * (1 - FEE_PCT);
  const isMe = !!myUser && winner?.id === myUser.id;

  const { value: countValue, done: countDone } = useCountUp(isMe ? lastPayout : 0, 1000);
  const { displayed: status, exiting } = useExitAwareStatus(rawStatus);
  const stateMod = exiting ? '--exit' : '--enter';

  return (
    <div className="game-status">
      {status === RollsStatusEnum.WAITING && (
        <div
          className={`game-status__content game-status__content--waiting game-status__content${stateMod}`}>
          <Users className="game-status__icon" />
          <span>Waiting for players</span>
          <div className="game-status__waiting-dots">
            <span className="game-status__waiting-dot" />
            <span className="game-status__waiting-dot" />
            <span className="game-status__waiting-dot" />
          </div>
        </div>
      )}

      {status === RollsStatusEnum.COUNTDOWN && countdown !== null && (
        <div
          className={`game-status__content game-status__content--countdown game-status__content${stateMod}`}>
          <div className="game-status__timer-ring">
            <svg className="game-status__timer-svg" viewBox="0 0 36 36">
              <circle
                className="game-status__timer-track"
                cx="18"
                cy="18"
                r="15.5"
                fill="none"
                strokeWidth="2"
              />
              <circle
                className="game-status__timer-progress"
                cx="18"
                cy="18"
                r="15.5"
                fill="none"
                strokeWidth="2.5"
                strokeDasharray="97.4"
                strokeDashoffset={97.4 - (97.4 * countdown) / 10}
                strokeLinecap="round"
              />
            </svg>
            <span key={countdown} className="game-status__timer-number game-status__timer-number--in">
              {countdown}
            </span>
          </div>
          <div className="game-status__countdown-text">
            <span className="game-status__countdown-label">Round starts in</span>
            <span className="game-status__countdown-pot">
              {formatCurrencyBreakdown(potsByCurrency)}{' '}
              <span className="game-status__countdown-pot-label">
                {formatUsd(totalPot)} in pot
              </span>
            </span>
          </div>
        </div>
      )}

      {status === RollsStatusEnum.SPINNING && (
        <div
          className={`game-status__content game-status__content--spinning game-status__content${stateMod}`}>
          <div className="game-status__spinning-shimmer" />
          <div className="game-status__spinning-inner">
            {spinPlayer && (
              <div
                key={spinPlayer.id + spinPlayer.username}
                className="game-status__spin-player game-status__spin-player--in">
                <PlayerAvatar
                  id={spinPlayer.id}
                  avatar={spinPlayer.avatar}
                  color={spinPlayer.color}
                  size={24}
                />
                <span className="game-status__spinning-label">@{spinPlayer.username}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {status === RollsStatusEnum.FINISHED && winner && (
        <div
          className={`game-status__content game-status__content--finished game-status__content${stateMod} ${isMe ? 'game-status__content--winner-me' : ''}`}>
          {isMe && <div className="game-status__me-shimmer" />}

          <div className="game-status__winner-trophy">
            <Trophy className="game-status__icon game-status__icon--trophy" />
          </div>

          {!isMe && (
            <PlayerAvatar id={winner.id} avatar={winner.avatar} color={winner.color} size={28} />
          )}

          <div className="game-status__winner-info">
            {isMe ? (
              <span className="game-status__winner-name game-status__winner-name--me">
                {getRankLabel(lastPayout)}
              </span>
            ) : (
              <>
                <span className="game-status__winner-label">Winner</span>
                <span className="game-status__winner-name">@{winner.username}</span>
              </>
            )}
          </div>

          <div className="game-status__prize-wrap game-status__prize-wrap--in">
            <span
              className={`game-status__prize ${isMe ? 'game-status__prize--me' : ''}${isMe && countDone ? ' game-status__prize--pop' : ''}`}>
              {isMe ? `+${formatUsd(countValue)}` : `+${formatUsd(lastPayout)}`}
            </span>
            <span className="game-status__prize-fee">{FEE_LABEL}</span>
          </div>
        </div>
      )}
    </div>
  );
}
